import { Box, Container, Flex, Grid, GridItem, Text, useColorModeValue } from '@chakra-ui/react';
import { getFormattedDatetime } from '../../components/Format';
import { convertSecondsToHHMM } from '../../utils/dateUtils';

type DataItem = {
  label: string;
  value: string | number | undefined;
};

export default function LearnerInfo({
  paymentLearner,
  dailyDurationSession,
  totalDuration,
  totalLessons,
  participatedLessons,
}: any) {
  const { m_learner, m_company } = paymentLearner || {};
  const { full_name, last_login_datetime } = m_learner || {};
  const { company_name } = m_company || {};
  const today = new Date();
  const currentDayOfWeek = today.getDay();
  const todayString = today.toISOString().split('T')[0];
  // Get the start of current week (Monday)
  const startOfWeek = new Date(today);
  const daysFromMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
  startOfWeek.setDate(today.getDate() - daysFromMonday);

  const dailyTimeSpent = [...Array(7)].map((_, index) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + index);
    const dateString = date.toISOString().split('T')[0];

    // Check if this day is in the past or today
    const isPastOrToday = date <= today;

    // Find the data for this date
    const foundData = dailyDurationSession.find((d: any) => d.date === dateString);

    // Show actual seconds for past/today, 0 for future
    const seconds =
      isPastOrToday && foundData ? parseInt(foundData.total_duration_seconds) || 0 : 0;

    return {
      day: date.toLocaleDateString('ja-JP', { weekday: 'short' }),
      seconds,
      isToday: dateString === todayString,
      date: dateString,
    };
  });

  const totalSecondsOfWeek = dailyTimeSpent.reduce((sum, item) => sum + item.seconds, 0);

  const UserInformation: DataItem[] = [
    { label: 'フルネーム', value: full_name },
    { label: '所属', value: company_name },
    {
      label: '最終ログイン',
      value: last_login_datetime
        ? getFormattedDatetime(String(last_login_datetime))
        : 'ログイン情報がありません',
    },
  ];

  const desiredWeeklyMinutes = 420;
  const desiredWeeklySeconds = desiredWeeklyMinutes * 60;

  // Progress value for the progress circle
  const progressValue = Math.min((totalSecondsOfWeek / desiredWeeklySeconds) * 100, 100);

  return (
    <Box
      p={'6'}
      border={'1px solid #ddd'}
      borderColor={useColorModeValue('#ddd', '#ddd')}
      rounded={'md'}
      className="Learner-Stat"
    >
      <Container p={3} maxW={'100%'}>
        <Grid
          templateColumns={{
            base: '1fr',
            md: '0.4fr 1fr 1fr',
          }}
          gap={8}
        >
          {/* 学習者情報 */}
          <GridItem textAlign={{ base: 'left', md: 'left' }} alignSelf="center">
            <Box>
              {UserInformation.map((item, index) => (
                <Box key={item.label} mb={index === 5 ? 0 : 5}>
                  <Flex direction={{ base: 'row', md: 'column' }}>
                    <Text
                      fontWeight="bold"
                      fontSize={{ base: 'sm', md: 'md' }}
                      mb={{ base: 0, md: 1 }}
                      flex={{ base: '1 1 auto', md: 'none' }}
                    >
                      {item.label}
                    </Text>
                    <Text
                      fontSize={{ base: 'sm', md: 'md' }}
                      flex={{ base: '1 1 auto', md: 'none' }}
                      textAlign={{ base: 'right', md: 'left' }}
                      whiteSpace={'pre-wrap'}
                    >
                      {item.value}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Box>
          </GridItem>

          {/* Time Spent */}
          <GridItem display="flex" flexDirection="row" justifyContent="center" gap={8}>
            <Text fontWeight="bold" textAlign="center" mt={{ base: 4, md: 0 }}></Text>
            {/* 学習時間ブロック */}
            <Flex direction="column" align="center" flex="1">
              <Text fontWeight="bold" mb={8}>
                学習時間
              </Text>
              <Flex justifyContent="center" gap={8}>
                {/* Weekly Time Spent */}
                <Flex direction="column" align="center">
                  <Box
                    position="relative"
                    width={{ base: '100px', md: '200px' }}
                    height={{ base: '100px', md: '200px' }}
                  >
                    <Box
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%"
                      height="100%"
                      borderRadius="full"
                      bg={`conic-gradient(
              teal ${progressValue * 3.6}deg,
              #edebe9 ${progressValue * 3.6}deg 360deg
              )`}
                      transform="rotate(0deg)"
                    />
                    <Box
                      position="absolute"
                      top="10%"
                      left="10%"
                      width="80%"
                      height="80%"
                      bg="white"
                      borderRadius="full"
                    />
                    <Flex
                      position="absolute"
                      top="0"
                      left="0"
                      width="100%"
                      height="100%"
                      align="center"
                      justify="center"
                    >
                      <Text fontSize={{ base: 'xl', md: '4xl' }}>
                        {convertSecondsToHHMM(totalSecondsOfWeek)}
                      </Text>
                    </Flex>
                  </Box>
                  <Text fontWeight="bold" mt={2}>
                    今週の学習時間
                  </Text>
                </Flex>

                {/* Total Time Spent All Time */}
                <Flex direction="column" align="center">
                  <Box
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="teal.500"
                    borderRadius="full"
                    borderColor="#edebe9"
                    borderWidth={15}
                    width={{ base: '100px', md: '200px' }}
                    height={{ base: '100px', md: '200px' }}
                  >
                    <Text color="white" fontSize={{ base: 'xl', md: '4xl' }}>
                      {convertSecondsToHHMM(totalDuration)}
                    </Text>
                  </Box>
                  <Text fontWeight="bold" mt={2}>
                    トータル学習時間
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </GridItem>
          <GridItem display="flex" flexDirection="row" justifyContent="center" gap={8}>
            {/* 実施済回数／全体回数 */}
            <Flex direction="column" align="center" flex="1">
              <Text fontWeight="bold" mb={8} whiteSpace="nowrap">
                実施済回数／全体回数
              </Text>
              <Flex direction="column" align="center">
                <Box
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="white"
                  borderRadius="full"
                  borderColor="#edebe9"
                  borderWidth={15}
                  width={{ base: '100px', md: '200px' }}
                  height={{ base: '100px', md: '200px' }}
                >
                  {/* Main bar */}
                  {/* 左上：10回 */}
                  <Text
                    position="absolute"
                    top="20%"
                    left="15%"
                    fontSize={{ base: 'xl', md: '4xl' }}
                    fontWeight="bold"
                  >
                    {participatedLessons}
                    <Box as="span" fontSize={{ base: 'sm', md: 'lg' }}>
                      回
                    </Box>
                  </Text>

                  {/* 斜め線 */}
                  <Box
                    position="absolute"
                    width="60%"
                    height="3px"
                    bg="black"
                    transform="rotate(-45deg)"
                  />

                  {/* 右下：12回 */}
                  <Text
                    position="absolute"
                    bottom="30%"
                    right="10%"
                    fontSize={{ base: 'xl', md: '4xl' }}
                    fontWeight="bold"
                  >
                    {totalLessons}
                    <Box as="span" fontSize={{ base: 'sm', md: 'lg' }}>
                      回
                    </Box>
                  </Text>
                </Box>
                <Text fontWeight="bold" mt={2} whiteSpace="nowrap">
                  オンラインレッスン実施状況
                </Text>
              </Flex>
            </Flex>
          </GridItem>

          {/* Daily Time Spent は一旦非表示 */}
          {/*
          <GridItem borderTop={{ base: '1px', md: 'none' }} borderColor={'orange.100'}>
            <Flex direction="column" align="center" h="full" justifyContent="space-between" gap={4}>
              <Text fontWeight="bold" m={{ base: 4, md: 0 }}>
                今週の学習時間
              </Text>
              <Grid templateColumns="repeat(7, 1fr)" gap={2} w="full" justifyItems="flex-end">
                {dailyTimeSpent.map((data) => (
                  <Box key={data.day} textAlign="center" justifySelf={'center'}>
                    ...
                  </Box>
                ))}
              </Grid>
            </Flex>
          </GridItem>
          */}
        </Grid>
      </Container>
    </Box>
  );
}
